<div class="p-3 h-[calc(100%-var(--spacing)*15)]">
  <app-breadcrumb [items]="[{ label: 'Reporting' }, { label: 'Customer Search' }]"/>
  <div class="h-[calc(100%-var(--spacing)*9)] rounded-sm border border-stroke bg-white shadow-default dark:border-strokedark dark:bg-boxdark">
    <div class="h-full w-full overflow-y-scroll">
      @if (loadingPredefinedValues()) {
      <div class="flex h-full flex-col items-center justify-center gap-3 text-gray-400">
        <svg class="h-8 w-8 animate-spin text-blue-500" viewBox="0 0 24 24" fill="none"><circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"/><path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8H4z"/></svg>
        <p class="text-sm">Loading...</p>
      </div>
      } @else {

      <!-- Search form -->
      <div class="border-b border-stroke px-5 py-4 dark:border-strokedark sm:px-7.5">
        <h4 class="text-lg font-semibold text-gray-800 dark:text-gray-300">Customer Search</h4>
        <p class="mt-1 text-sm text-gray-500 dark:text-gray-400">
          Search customer records by any of the fields below. Leave a field blank to ignore it.
        </p>

        <div class="mt-4 flex flex-wrap items-end gap-6">
          <div class="w-full max-w-xs">
            <label class="mb-1 block text-xs text-gray-500 dark:text-gray-400">Organization</label>
            <select [(ngModel)]="organizationId" (change)="onOrganizationChange()"
              class="h-9 w-full rounded-lg border border-gray-300 bg-white px-2 text-sm text-gray-800 shadow-sm focus:outline-none dark:border-gray-700 dark:bg-gray-900 dark:text-white">
              <option value="" disabled>Select organization</option>
              @for (org of organizationStore.organizations(); track org.id) {
                <option [value]="org.id">{{ org.name }}</option>
              }
            </select>
          </div>
          <div>
            <label class="mb-1 block text-xs text-gray-500 dark:text-gray-400">Sort Direction</label>
            <label class="flex h-9 cursor-pointer items-center gap-2">
              <span class="relative inline-flex items-center">
                <input type="checkbox" class="peer sr-only"
                  [ngModel]="searchSortDirection === 'desc'"
                  (ngModelChange)="searchSortDirection = $event ? 'desc' : 'asc'"/>
                <span class="h-6 w-11 rounded-full bg-gray-300 transition-colors peer-checked:bg-brand-500 dark:bg-gray-700"></span>
                <span class="absolute left-1 h-4 w-4 rounded-full bg-white shadow transition-transform peer-checked:translate-x-5"></span>
              </span>
              <span class="text-sm text-gray-700 dark:text-gray-300">{{ searchSortDirection === 'desc' ? 'Descending' : 'Ascending' }}</span>
            </label>
          </div>
        </div>

        <div class="mt-4 grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-4">
          @for (rf of rangeFields; track rf.key) {
            <div>
              <label class="mb-1 block text-xs text-gray-500 dark:text-gray-400">{{ rf.label }} &mdash; From</label>
              <input type="date" [(ngModel)]="criteria[rf.key + 'From']" [name]="rf.key + 'From'"
                class="h-9 w-full rounded-lg border border-gray-300 bg-transparent px-2 text-sm text-gray-800 shadow-sm focus:outline-none dark:border-gray-700 dark:text-white scheme-light dark:scheme-dark"/>
            </div>
            <div>
              <label class="mb-1 block text-xs text-gray-500 dark:text-gray-400">{{ rf.label }} &mdash; To</label>
              <input type="date" [(ngModel)]="criteria[rf.key + 'To']" [name]="rf.key + 'To'"
                class="h-9 w-full rounded-lg border border-gray-300 bg-transparent px-2 text-sm text-gray-800 shadow-sm focus:outline-none dark:border-gray-700 dark:text-white scheme-light dark:scheme-dark"/>
            </div>
          }
          @for (field of criteriaFields; track field) {
            <div>
              <div class="mb-1 flex items-center gap-1 text-xs text-gray-500 dark:text-gray-400">
                {{ formatFieldLabel(field) }}
                @if (likeFields.includes(field)) {
                  <app-info-tooltip>This performs a "contains" (Like) search — a partial {{ formatFieldLabel(field).toLowerCase() }} will match.</app-info-tooltip>
                }
              </div>
              @if (field === 'alertRaised') {
                <select [(ngModel)]="criteria[field]"
                  class="h-9 w-full rounded-lg border border-gray-300 bg-white px-2 text-sm text-gray-800 shadow-sm focus:outline-none dark:border-gray-700 dark:bg-gray-900 dark:text-white">
                  <option value="">Any</option>
                  <option value="true">Yes</option>
                  <option value="false">No</option>
                </select>
              } @else if (field === 'surveyId') {
                <select [(ngModel)]="criteria[field]"
                  class="h-9 w-full rounded-lg border border-gray-300 bg-white px-2 text-sm text-gray-800 shadow-sm focus:outline-none dark:border-gray-700 dark:bg-gray-900 dark:text-white">
                  <option value="">Any</option>
                  @for (survey of surveys; track survey.id) {
                    <option [value]="survey.id">{{ survey.name }}</option>
                  }
                </select>
              } @else if (field === 'segmentId') {
                <select [(ngModel)]="criteria[field]"
                  class="h-9 w-full rounded-lg border border-gray-300 bg-white px-2 text-sm text-gray-800 shadow-sm focus:outline-none dark:border-gray-700 dark:bg-gray-900 dark:text-white">
                  <option value="">Any</option>
                  @for (segment of segments; track segment) {
                    <option [value]="segment">{{ segment }}</option>
                  }
                </select>
              } @else if (isNullAware(field)) {
                <select [(ngModel)]="criteria[field]"
                  class="h-9 w-full rounded-lg border border-gray-300 bg-white px-2 text-sm text-gray-800 shadow-sm focus:outline-none dark:border-gray-700 dark:bg-gray-900 dark:text-white">
                  <option value="">Any</option>
                  @if (hasNullOption(field)) {
                    <option [value]="NULL_OPTION">Null</option>
                  }
                  @for (option of predefinedOptions(field); track option) {
                    <option [value]="option">{{ option }}</option>
                  }
                </select>
              } @else {
                <input type="text" [(ngModel)]="criteria[field]" [name]="field"
                  class="h-9 w-full rounded-lg border border-gray-300 bg-transparent px-2 text-sm text-gray-800 shadow-sm focus:outline-none dark:border-gray-700 dark:text-white"/>
              }
            </div>
          }
        </div>

        <div class="mt-5 flex items-center gap-3">
          <button type="button" (click)="search()" [disabled]="!canSearch()"
            class="flex items-center gap-2 rounded-lg bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700 disabled:cursor-not-allowed disabled:opacity-50">
            @if (loading()) {
              <svg class="h-4 w-4 animate-spin" viewBox="0 0 24 24" fill="none"><circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"/><path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8H4z"/></svg>
              Searching...
            } @else {
              Search
            }
          </button>
          <button type="button" (click)="clear()" [disabled]="loading()"
            class="rounded-lg border border-gray-300 px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 disabled:opacity-50 dark:border-gray-700 dark:text-gray-300 dark:hover:bg-gray-800">
            Clear
          </button>
          @if (!organizationId) {
            <p class="text-xs text-red-500">Select an organization.</p>
          }
        </div>
      </div>

      <!-- Results -->
      <div class="p-5">
        @if (error()) {
          <p class="mb-3 rounded-lg border border-red-200 bg-red-100 p-3 text-sm text-red-600">{{ error() }}</p>
        }

        @if (!hasSearched()) {
          <div class="flex flex-col items-center justify-center py-16 text-gray-400">
            <svg xmlns="http://www.w3.org/2000/svg" class="mb-3 h-10 w-10 opacity-40" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round">
              <circle cx="11" cy="11" r="8"/><path d="m21 21-4.35-4.35"/>
            </svg>
            <p class="text-sm">Enter search criteria above, then click Search.</p>
          </div>
        } @else if (loading()) {
          <div class="flex justify-center py-16">
            <svg class="h-8 w-8 animate-spin text-blue-500" viewBox="0 0 24 24" fill="none"><circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"/><path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8H4z"/></svg>
          </div>
        } @else {
          <div class="mb-3 flex flex-wrap items-center justify-between gap-3">
            <h3 class="text-sm font-semibold text-gray-700 dark:text-gray-200">
              Results
              <span class="ml-1.5 text-xs font-normal text-gray-400">({{ totalCount() }} total)</span>
              @if (hasClickableRows) {
                <span class="ml-3 inline-flex items-center gap-1 text-xs font-normal text-gray-400">
                  <svg class="h-3.5 w-3.5 text-blue-500" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M2 12s3.5-7 10-7 10 7 10 7-3.5 7-10 7S2 12 2 12z"/><circle cx="12" cy="12" r="3"/></svg>
                  Click a row to view customer details
                </span>
              }
            </h3>
            <div class="flex flex-wrap items-center gap-2">
              <label class="text-xs text-gray-500 dark:text-gray-400">Per page</label>
              <select [(ngModel)]="perPage" (change)="onPerPageChange()"
                class="h-8 rounded-lg border border-gray-300 bg-white px-2 text-sm dark:border-gray-700 dark:bg-gray-900 dark:text-white">
                @for (opt of perPageOptions; track opt) {
                  <option [ngValue]="opt">{{ opt }}</option>
                }
              </select>
            </div>
          </div>

          <div class="max-h-[65vh] pb-4 overflow-auto rounded-lg border border-gray-200 dark:border-gray-700">
            <table class="w-full text-left text-sm text-gray-700 dark:text-gray-300">
              <thead class="sticky top-0 z-10 bg-gray-50 text-xs uppercase text-gray-500 dark:bg-gray-800 dark:text-gray-400">
                <tr>
                  <!-- Details indicator column — sticky so it stays visible when the table scrolls sideways. -->
                  <th class="sticky left-0 w-8 bg-gray-50 px-2 py-2.5 dark:bg-gray-800"><span class="sr-only">Details</span></th>
                  @for (field of searchFields; track field) {
                    <th class="whitespace-nowrap px-3 py-2.5">
                      <button type="button" (click)="sortBy(field)" class="flex items-center gap-1 uppercase hover:text-gray-800 dark:hover:text-gray-200">
                        {{ formatFieldLabel(field) }}
                        <span class="text-[9px] text-blue-500">{{ sortIndicator(field) }}</span>
                      </button>
                    </th>
                  }
                </tr>
              </thead>
              <tbody>
                @for (item of sortedItems; track $index) {
                  <tr class="group border-t border-gray-100 dark:border-gray-800"
                    [class.cursor-pointer]="hasSurveyRequestId(item)" [class.hover:bg-blue-50]="hasSurveyRequestId(item)"
                    [class.dark:hover:bg-gray-800]="hasSurveyRequestId(item)"
                    [title]="hasSurveyRequestId(item) ? 'Click to view customer details' : ''"
                    (click)="openDetails(item)">
                    <!-- Needs its own background as a sticky cell, so it mirrors the row hover via group-hover. -->
                    <td class="sticky left-0 bg-white px-2 py-2 dark:bg-boxdark"
                      [class.group-hover:bg-blue-50]="hasSurveyRequestId(item)" [class.dark:group-hover:bg-gray-800]="hasSurveyRequestId(item)">
                      @if (hasSurveyRequestId(item)) {
                        <svg class="h-4 w-4 text-blue-500 group-hover:text-blue-700" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M2 12s3.5-7 10-7 10 7 10 7-3.5 7-10 7S2 12 2 12z"/><circle cx="12" cy="12" r="3"/></svg>
                      }
                    </td>
                    @for (field of searchFields; track field) {
                      <td class="whitespace-nowrap px-3 py-2 text-xs">
                        @if (field === 'alertRaised') {
                          <span class="rounded-full px-2 py-0.5 text-xs font-medium"
                            [class.bg-red-100]="item.alertRaised" [class.text-red-700]="item.alertRaised"
                            [class.bg-gray-100]="!item.alertRaised" [class.text-gray-500]="!item.alertRaised">
                            {{ item.alertRaised ? 'Yes' : 'No' }}
                          </span>
                        } @else if (field === 'surveyLink') {
                          @if (item.surveyLink) {
                            <a [href]="item.surveyLink + '/E'" target="_blank" rel="noopener" (click)="$event.stopPropagation()" class="text-blue-600 hover:underline">Link</a>
                          } @else {
                            <span class="text-gray-300">&mdash;</span>
                          }
                        } @else {
                          {{ getFieldValue(item, field) || '—' }}
                        }
                      </td>
                    }
                  </tr>
                } @empty {
                  <tr><td [attr.colspan]="searchFields.length + 1" class="px-3 py-8 text-center text-gray-400">No customers found.</td></tr>
                }
              </tbody>
            </table>
          </div>

          @if (totalCount() > 0) {
            <div class="mt-4 flex items-center justify-between text-sm text-gray-600 dark:text-gray-400">
              <span>{{ totalCount() }} result{{ totalCount() === 1 ? '' : 's' }} &mdash; page {{ currentPage() }} of {{ totalPages }}</span>
              <div class="flex gap-1">
                <button type="button" (click)="changePage(currentPage() - 1)"
                  [disabled]="currentPage() === 1"
                  class="rounded-lg border border-gray-300 px-3 py-1.5 text-xs hover:bg-gray-100 disabled:opacity-40 disabled:cursor-not-allowed dark:border-gray-700 dark:hover:bg-gray-800">
                  Prev
                </button>
                <button type="button" (click)="changePage(currentPage() + 1)"
                  [disabled]="currentPage() === totalPages"
                  class="rounded-lg border border-gray-300 px-3 py-1.5 text-xs hover:bg-gray-100 disabled:opacity-40 disabled:cursor-not-allowed dark:border-gray-700 dark:hover:bg-gray-800">
                  Next
                </button>
              </div>
            </div>
          }
        }
      </div>

      }
    </div>
  </div>
</div>

@if (detailsSurveyRequestId(); as surveyRequestId) {
  <app-customer-details-dialog [surveyRequestId]="surveyRequestId" (closed)="detailsSurveyRequestId.set(null)"/>
}
